import React from 'react';

import {
  Checkbox,
  CheckboxGroup,
  Field,
  Grid,
  Margin,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import {
  PUBLISHING_FORM_FIELD_BLANK_VALUES as FIELD_BLANK_VALUES,
  useForm,
} from 'app/developer-v3/hooks';

import AccountCreationHelp from './AccountCreationHelp';
import Step from './Step';
import { useStepCompletion } from './useStepCompletion';
import { getError, parseSaveErrors } from './utils';

import type { TestAccountValues as Values } from './types';

type Props = {
  initialValues: Values;
  isDisabled: boolean;
  onComplete: (isComplete: boolean) => void;
  onSubmit: (valuesNext: Values) => Promise<void>;
};

export default function TestAccount({
  initialValues,
  isDisabled,
  onComplete,
  onSubmit,
}: Props) {
  const form = useForm<Values>({
    enableReinitialize: true,
    initialValues,
    onSubmit: async formValues => {
      try {
        await onSubmit(formValues);
      } catch (errors) {
        Object.entries(parseSaveErrors(errors)).forEach(([key, value]) => {
          form.setFieldError(key, value as string);
        });
      }
    },
    validate: (values: Values) => {
      const errors: Partial<Record<keyof Values, string>> = {};
      if (values.testAccountOnlySupportsSso) {
        if (!values.ssoTestAccountCreated) {
          errors.ssoTestAccountCreated = 'Required.';
        }
      } else {
        if (
          values.testAccountUsername === FIELD_BLANK_VALUES.testAccountUsername
        ) {
          errors.testAccountUsername = 'Test account username is required.';
        }
        if (values.testAccountUsername !== 'integration-testing@zapier.com') {
          errors.testAccountUsername = 'Test account username isn’t right.';
        }
        if (
          values.testAccountPassword === FIELD_BLANK_VALUES.testAccountPassword
        ) {
          errors.testAccountPassword = 'Test account password is required.';
        }
      }
      return errors;
    },
    validateOnMount: true,
  });

  const isTouched = Object.values(form.isTouched).some(value => value);

  const isCompleted = !form.isDirty && form.isValid;
  useStepCompletion(isCompleted, onComplete);

  return (
    <Step
      isComplete={!form.isDirty && form.isValid}
      isError={isTouched && !form.isValid}
      title="Test account for us"
    >
      <AccountCreationHelp />
      <Spacer height="20px" />
      <Grid columns="1" gap="20px">
        {!form.values.testAccountOnlySupportsSso && (
          <Grid alignItems="start" columns="repeat(2, auto)" gap="40px">
            <Field
              error={getError(
                'testAccountUsername',
                form.isTouched,
                form.errors
              )}
              isDisabled={isDisabled}
              isRequired={true}
              label="Username"
              renderHelpText={() => (
                <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                  Username MUST be integration-testing@zapier.com.
                </Text>
              )}
              renderInput={inputProps => (
                <TextInput
                  {...inputProps}
                  aria-label="Test account username"
                  name="testAccountUsername"
                  onBlur={form.onBlur}
                  onChange={form.onChange}
                  value={form.values.testAccountUsername}
                />
              )}
            />
            <Field
              error={getError(
                'testAccountPassword',
                form.isTouched,
                form.errors
              )}
              isDisabled={isDisabled}
              isRequired={true}
              label="Password"
              renderInput={inputProps => (
                <TextInput
                  {...inputProps}
                  aria-label="Test account password"
                  name="testAccountPassword"
                  onBlur={form.onBlur}
                  onChange={form.onChange}
                  value={form.values.testAccountPassword}
                />
              )}
            />
          </Grid>
        )}
        <CheckboxGroup
          errorMessage={
            getError(
              'testAccountOnlySupportsSso',
              form.isTouched,
              form.errors
            ) || undefined
          }
          helpText="We prefer username/password or magic link credentials. But if that's not possible for your app, check this box."
          isDisabled={isDisabled}
          isInvalid={Boolean(
            getError('testAccountOnlySupportsSso', form.isTouched, form.errors)
          )}
          isRequired={false}
          label="SSO login only"
          name="testAccountOnlySupportsSso"
          onChange={keys => {
            form.setFieldTouched('testAccountOnlySupportsSso', true);
            form.setFieldValue(
              'testAccountOnlySupportsSso',
              keys.includes('only-sso')
            );
          }}
          value={form.values.testAccountOnlySupportsSso ? ['only-sso'] : []}
        >
          <Checkbox value="only-sso">My app only supports SSO login.</Checkbox>
        </CheckboxGroup>
        {form.values.testAccountOnlySupportsSso && (
          <Margin margin="0px 0px 0px 20px">
            <Grid columns="1" gap="20px">
              <CheckboxGroup
                errorMessage={
                  getError(
                    'ssoTestAccountCreated',
                    form.isTouched,
                    form.errors
                  ) || undefined
                }
                helpText={
                  <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                    Create a test account using the email address{' '}
                    <code>test.integration.sso@zapier.com</code>.
                  </Text>
                }
                isDisabled={isDisabled}
                isInvalid={Boolean(
                  getError('ssoTestAccountCreated', form.isTouched, form.errors)
                )}
                isRequired={true}
                label="Create a SSO test account."
                name="ssoTestAccountCreated"
                onChange={keys => {
                  form.setFieldTouched('ssoTestAccountCreated', true);
                  form.setFieldValue(
                    'ssoTestAccountCreated',
                    keys.includes('sso-created')
                  );
                }}
                value={form.values.ssoTestAccountCreated ? ['sso-created'] : []}
              >
                <Checkbox value="sso-created">
                  I have created the SSO test account.
                </Checkbox>
              </CheckboxGroup>
            </Grid>
          </Margin>
        )}
        <Field
          error={getError('testAccountNotes', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={false}
          label="Notes"
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="Test account notes"
              isMultiline={true}
              name="testAccountNotes"
              onBlur={form.onBlur}
              onChange={form.onChange}
              rows={4}
              value={form.values.testAccountNotes}
            />
          )}
        />
      </Grid>
      <Spacer height="20px" />
      <AsyncButton
        aria-label="Save test account"
        isDisabled={
          isDisabled || !form.isDirty || !form.isValid || form.isSubmitting
        }
        isWorking={form.isSubmitting}
        onPress={() => form.onSubmit()}
        type="button"
        variant="secondary"
      >
        Save
      </AsyncButton>
    </Step>
  );
}
